// Pure helpers for the "Failed numbers" tab of the SMS Status modal. Rows come
// from public.sms_number_health (see sql/migrations/2026-09-22-sms-number-health.sql),
// which a pg_cron job rebuilds from each schema's "SMS" table every 5 minutes.

// What a row means to the person cleaning up patient records. Order matters:
// it is the order of the filter chips.
export const FAILED_NUMBER_STATUSES = {
  bad: {
    label: "Bad number",
    colorScheme: "red",
    action: "Correct or replace the number on file",
  },
  opt_out: {
    label: "Opted out",
    colorScheme: "purple",
    action: "Do not text — the patient opted out",
  },
  failing: {
    label: "Failing",
    colorScheme: "orange",
    action: "Texts are not getting through — check the error below",
  },
  recovered: {
    label: "Recovered",
    colorScheme: "green",
    action: "Delivering again — no action needed",
  },
};

// Why a "failing" number is failing, by error severity (sms_error_codes.severity).
const FAILING_ACTIONS = {
  transient: "Carrier hiccup — usually clears on its own; retry later",
  content: "Our message was rejected — shorten or reword it",
  config: "Account/campaign problem on our side — not the patient's number",
};

export const NEEDS_ATTENTION = ["bad", "opt_out", "failing"];

export const failedNumberStatus = (row) => {
  if (row.verdict === "opt_out") return "opt_out";
  if (row.verdict === "good") return "recovered";
  if (row.verdict === "bad" && row.error_severity === "permanent") return "bad";
  return "failing";
};

export const recommendedAction = (row) => {
  const status = failedNumberStatus(row);
  if (status === "opt_out" && !row.on_do_not_text && row.error_severity === "opt_out") {
    return "Patient replied STOP — do not retry";
  }
  if (status === "failing" && FAILING_ACTIONS[row.error_severity]) {
    return FAILING_ACTIONS[row.error_severity];
  }
  return FAILED_NUMBER_STATUSES[status].action;
};

// "+15551234567" -> "(555) 123-4567"; anything else is shown as stored.
export const formatPhone = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  const national = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (national.length !== 10) return phone || "";
  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
};

// Spellings the same number may be stored under in a schema's "SMS"."to".
export const phoneVariants = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  const national = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  const out = new Set([phone, digits, national, `+${digits}`]);
  if (national.length === 10) {
    out.add(`+1${national}`);
    out.add(`1${national}`);
    out.add(formatPhone(national));
  }
  return [...out].filter(Boolean);
};

// "2026-09-01" -> "Sep 1, 2026", without the UTC-midnight shift new Date() gives.
export const formatVisitDate = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  if (!m) return value || "";
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const timeAgo = (iso, now = Date.now()) => {
  if (!iso) return "—";
  const seconds = Math.round((now - new Date(iso).getTime()) / 1000);
  if (!Number.isFinite(seconds)) return "—";
  if (seconds < 60) return "just now";
  const units = [
    ["year", 31536000],
    ["month", 2592000],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  for (const [unit, size] of units) {
    const n = Math.floor(seconds / size);
    if (n >= 1) return `${n} ${unit}${n === 1 ? "" : "s"} ago`;
  }
  return "just now";
};

export const summarizeFailedNumbers = (rows) => {
  const counts = { all: rows.length, attention: 0, bad: 0, opt_out: 0, failing: 0, recovered: 0 };
  let failedTexts = 0;
  rows.forEach((row) => {
    const status = failedNumberStatus(row);
    counts[status] += 1;
    if (NEEDS_ATTENTION.includes(status)) counts.attention += 1;
    failedTexts += row.failed_count || 0;
  });
  return { counts, failedTexts };
};

// Error codes across all rows, most numbers first: [{ code, numbers }].
export const errorCodeCounts = (rows) => {
  const byCode = new Map();
  rows.forEach((row) => {
    (row.distinct_error_codes || []).forEach((code) => {
      byCode.set(code, (byCode.get(code) || 0) + 1);
    });
  });
  return [...byCode.entries()]
    .map(([code, numbers]) => ({ code, numbers }))
    .sort((a, b) => b.numbers - a.numbers || a.code.localeCompare(b.code));
};

const SORTS = {
  recent: (a, b) => new Date(b.last_failure_at || 0) - new Date(a.last_failure_at || 0),
  failures: (a, b) => (b.failed_count || 0) - (a.failed_count || 0),
};

// status: "attention" | "all" | one of FAILED_NUMBER_STATUSES' keys.
export const filterFailedNumbers = (rows, { status = "attention", errorCode = "", search = "", sort = "recent" } = {}) => {
  const needle = search.trim().toLowerCase();
  const needleDigits = needle.replace(/\D/g, "");
  return rows
    .filter((row) => {
      const s = failedNumberStatus(row);
      if (status === "attention" && !NEEDS_ATTENTION.includes(s)) return false;
      if (status !== "attention" && status !== "all" && s !== status) return false;
      if (errorCode && !(row.distinct_error_codes || []).includes(errorCode)) return false;
      if (!needle) return true;
      if (needleDigits && String(row.phone_number || "").replace(/\D/g, "").includes(needleDigits)) return true;
      return [row.last_error_code, row.error_description, row.last_error_message, ...(row.patient_names || [])]
        .some((v) => String(v || "").toLowerCase().includes(needle));
    })
    .sort(SORTS[sort] || SORTS.recent);
};

const csvCell = (v) => {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const failedNumbersCsv = (rows) => {
  const header = [
    "Provider", "Patient", "Phone", "Status", "Recommended action", "Last error code", "Error description",
    "All error codes", "Failed texts", "Delivered texts", "Total texts",
    "Last failure", "Last success", "On Do Not Text",
  ];
  const lines = rows.map((row) => [
    row.schema_name,
    (row.patient_names || []).join("; "),
    formatPhone(row.phone_number),
    FAILED_NUMBER_STATUSES[failedNumberStatus(row)].label,
    recommendedAction(row),
    row.last_error_code,
    row.error_description || row.last_error_message,
    (row.distinct_error_codes || []).join(" "),
    row.failed_count,
    row.delivered_count,
    row.total_messages,
    row.last_failure_at,
    row.last_success_at,
    row.on_do_not_text ? "yes" : "no",
  ].map(csvCell).join(","));
  return [header.join(","), ...lines].join("\n");
};
